import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
import { Page, PageBreadcrumb, PageContent, TextButton } from '@zillow/constellation';
import { PropertyPageHeader, TAG_APPEARANCES, TAG_SIZES } from '@zg-rentals/zrs-comet';
import { BREADCRUMB_PATHS, PDP_HEADER_TAB_LABEL } from '../PDPHeader/constants';
import { ZRMUtils } from '@zg-rentals/rental-manager-header-navigation';
import type { Listing } from '../declarations/Listing';

const propTypes = {
  listing: PropTypes.object.isRequired,
  tabConfig: PropTypes.object.isRequired,
  statusConfig: PropTypes.shape({
    appearance: PropTypes.oneOf(Object.keys(TAG_APPEARANCES)).isRequired,
    message: PropTypes.string.isRequired,
    size: PropTypes.oneOf(Object.keys(TAG_SIZES)).isRequired,
  }),
  isInMobileWebView: PropTypes.bool.isRequired,
  handleBreadcrumbClick: PropTypes.func,
  customHrefValue: PropTypes.string,
  customDisplayName: PropTypes.string,
};

const defaultProps = {
  statusConfig: null,
  handleBreadcrumbClick: () => {
    // no-op
  },
  customHrefValue: null,
  customDisplayName: null,
};

const DEFAULT_KEY = PDP_HEADER_TAB_LABEL.LISTING;

const getFormattedAddress = (listing: Listing) => {
  const { city, state, street, unit, zip } = listing;

  const tempAddress = ZRMUtils.propertyUtils.formatAddress({
    street,
    city,
    state,
    unit,
    zip,
  });

  return `${tempAddress.formattedAddress} ${zip}`;
};

interface PDPConnectedOptions {
  customHrefValue?: string;
  customDisplayName?: string;
  handleBreadcrumbClick: () => void;
  isInMobileWebView: boolean;
  listing: Listing;
  statusConfig: {
    appearance: string;
    message: string;
    size: string;
  };
  tabConfig: {
    [key: string]: {
      tabComponent?: React.ReactNode;
      tabPath?: string;
    };
  };
}

const PDPConnected = ({
  handleBreadcrumbClick,
  isInMobileWebView,
  listing,
  statusConfig,
  tabConfig,
  customDisplayName,
  customHrefValue,
}: PDPConnectedOptions) => {
  const key = Object.keys(tabConfig).length === 1 ? Object.keys(tabConfig)[0] : DEFAULT_KEY;
  const configItem = tabConfig[key];
  const hasTabComponent =
    configItem &&
    Object.prototype.hasOwnProperty.call(configItem, 'tabComponent') &&
    typeof configItem.tabComponent !== 'undefined';

  const title = startCase(key);
  const displayName = customDisplayName || getFormattedAddress(listing);

  // determine the breadcrumb path based on tabConfig key
  const to = customHrefValue ? customHrefValue : BREADCRUMB_PATHS[key].replace(':listingAlias', listing.listingAlias);

  return (
    <Page backgroundColor="transparent" className="PDPConnected" data-testid="PDPConnected">
      {!isInMobileWebView && (
        <PageBreadcrumb
          backButton={
            <TextButton as="a" href={to} onClick={handleBreadcrumbClick}>
              {displayName}
            </TextButton>
          }
          data-testid="PageBreadcrumb"
        />
      )}
      <PageContent>
        <PropertyPageHeader title={title} statusConfig={statusConfig}>
          {/* @ts-ignore strict mode this fails */}
          {hasTabComponent && configItem.tabComponent}
        </PropertyPageHeader>
      </PageContent>
    </Page>
  );
};

PDPConnected.propTypes = propTypes;
PDPConnected.defaultProps = defaultProps;

export default PDPConnected;
